import { useEffect, useState, type ReactNode } from "react";

import "./panel.css";

import { cx } from "@/lib/cx";
import type { EnterProps } from "../motion";
import { Panel as LegacyPanel, type PanelProps as LegacyPanelProps } from "../Panel";
import { TONE_TEXT, type Tone } from "../tones";

export type CompactPanelProps = {
  /** The shared 16/600 section title. Every panel has one. */
  label: ReactNode;
  /** The right end of the header strip — a count, a date, a note. */
  aside?: ReactNode;
  /** Only for a streak or other quantity that expires tonight. */
  perishable?: boolean;
  /** Header text tint, retained for existing compact callers. */
  tone?: Tone;
  /** Gives prose or form content the compact 12px body inset. Rows stay flush. */
  padded?: boolean;
  /** A quiet line under the body, separated from it by one full-width hairline. */
  footnote?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Attribute-only entrance treatment; the panel remains in normal flow. */
  enter?: EnterProps;
  "aria-label"?: string;
  "aria-labelledby"?: string;
};

/**
 * The shared export also accepts the old panel props until the later control
 * conversion migrates those call sites. That branch delegates unchanged to
 * `ui/Panel`; a labelled panel always takes the compact R5/R6 path below.
 */
export type PanelProps = CompactPanelProps | LegacyPanelProps;

/**
 * The only container on a compact surface.
 *
 * A scene surface, a 16px radius, and the shared 40/44px header row. Its body is
 * `overflow-hidden` so a row's hover fill is clipped by the panel's own corner
 * instead of each row having to round itself — the same reason the grouped list
 * clips its rows, and the reason rows here are square. A prose or form panel
 * opts into `padded`; a `footnote` stays inside the same boundary under one
 * full-width hairline.
 */
export function Panel(props: PanelProps) {
  if (!("label" in props)) {
    return <LegacyPanel {...props} />;
  }

  return <CompactPanel {...props} />;
}

function CompactPanel(props: CompactPanelProps) {
  const {
    label,
    aside,
    tone,
    perishable = false,
    padded = false,
    footnote,
    children,
    className,
    enter,
    "aria-label": ariaLabel,
    "aria-labelledby": ariaLabelledBy,
  } = props;

  const [glowVisible, setGlowVisible] = useState(perishable);
  const [hasFrame, setHasFrame] = useState(perishable);
  useEffect(() => {
    if (perishable) {
      setGlowVisible(true);
      setHasFrame(true);
      return;
    }
    if (!glowVisible) return;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
      || document.documentElement.dataset.motion === "reduced";
    const timer = window.setTimeout(() => setGlowVisible(false), reduced ? 0 : 600);
    return () => window.clearTimeout(timer);
  }, [perishable, glowVisible]);
  // Keep the 2px inset after a saved streak becomes safe. Content never moves.
  const framed = perishable || hasFrame;

  return (
    <section
      {...enter}
      style={perishable ? { ...enter?.style, boxShadow: "var(--busee-perish-shadow)" } : enter?.style}
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
      className={cx(
        "busee-panel relative flex min-w-0 flex-col overflow-hidden",
        "rounded-surface",
        framed ? "busee-perishable" : "border-2 border-ps1-orange bg-ps1-panel",
        className,
      )}
    >
      {perishable || glowVisible ? (
        <span
          aria-hidden
          className={cx("busee-perishable-frame", !perishable && "busee-perishable-frame-releasing")}
          style={{ backgroundImage: "var(--busee-perish-glow)" }}
        />
      ) : null}
      <div className={cx("relative flex min-w-0 flex-col", framed && "busee-perishable-inner")}>
      <header className="busee-panel-head flex row-height shrink-0 items-center gap-2 hairline-b px-3 py-1.5">
        <h2
          className={cx(
            "min-w-0 flex-1 truncate text-section",
            "font-normal",
            perishable ? "text-[var(--ps1-red-text)]" : tone ? TONE_TEXT[tone] : "text-ps1-gold",
          )}
        >
          {label}
        </h2>
        {aside ? (
          <span className="busee-panel-aside shrink-0 text-label text-subtle">{aside}</span>
        ) : null}
      </header>

      <div className={cx("flex min-w-0 flex-col", padded && "p-3")}>{children}</div>
      {footnote ? (
        <p className="busee-panel-foot hairline-t px-3 py-2 text-caption text-subtle">{footnote}</p>
      ) : null}
      </div>
    </section>
  );
}
